/**
 * The open counter on each clause, as one party sees it.
 *
 * Clause cards only showed accepted values, so a pending counter (e.g. the
 * listing agent's $540,000 on a $530,000 offer) never appeared on the
 * seller's screens.
 */

type Side = 'buyer' | 'seller';

type ProposalLike = {
  _id: string;
  clauseId: string;
  proposedValue: any;
  status: string;
  proposedBy: string;
  responseRequiredFrom: string;
  message?: string;
  createdAt?: string;
  user?: { _id?: string; name?: string; role?: string } | string | null;
};

export interface PendingCounter {
  proposalId: string;
  proposedValue: any;
  /** "You", "Your agent", "Buyer", "Seller", "Seller's agent". */
  fromLabel: string;
  /** The viewer's side has to accept or counter it. */
  waitingOnYou: boolean;
  waitingOnLabel: string;
  message?: string;
}

const AGENT_ROLES = ['seller-agent', 'seller_agent', 'agent'];
const SIDE_LABEL: Record<string, string> = { buyer: 'Buyer', seller: 'Seller' };

function fromLabel(p: ProposalLike, viewer: Side, viewerId?: string): string {
  const author = typeof p.user === 'object' && p.user ? p.user : undefined;
  if (viewerId && author?._id === viewerId) return 'You';
  const byAgent = AGENT_ROLES.includes(author?.role ?? '');
  if (p.proposedBy === viewer) return byAgent ? 'Your agent' : 'Your side';
  if (byAgent) return "Seller's agent";
  return SIDE_LABEL[p.proposedBy] ?? 'Other party';
}

/** clauseId → the newest pending counter on it. */
export function pendingCountersByClause(
  proposals: ProposalLike[] | undefined,
  viewer: Side,
  viewerId?: string
): Map<string, PendingCounter> {
  const newestFirst = [...(proposals ?? [])]
    .filter((p) => p.status === 'pending')
    .sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime()
    );
  const map = new Map<string, PendingCounter>();
  for (const p of newestFirst) {
    if (map.has(p.clauseId)) continue;
    map.set(p.clauseId, {
      proposalId: p._id,
      proposedValue: p.proposedValue,
      fromLabel: fromLabel(p, viewer, viewerId),
      waitingOnYou: p.responseRequiredFrom === viewer,
      waitingOnLabel:
        p.responseRequiredFrom === viewer
          ? 'you'
          : (
              SIDE_LABEL[p.responseRequiredFrom] ?? 'the other party'
            ).toLowerCase(),
      message: p.message,
    });
  }
  return map;
}
